import { api, asList } from './api';

/**
 * ═══ MASTER DATA ═══
 *
 * The small lists a school types once at setup and then lives with for a decade: subjects, classes,
 * rooms, departments, designations, leave types, vehicles, hostel blocks, store items, sports
 * equipment, campuses, institutions.
 *
 * ⚠ WHY THIS FILE EXISTS AT ALL. Every one of these could be CREATED from some screen and then
 * never corrected. The domain had the methods — `Subject.Update`, `Vehicle.Update`,
 * `Room.Deactivate`, `HostelBlock.Update`, `StoreItem.Update`, `Exam.Update` — written, commented,
 * and unreachable, because no endpoint ever called them and no screen ever called an endpoint. A
 * typo in a subject name printed on every marksheet for as long as the school used Eduvora.
 *
 * ⚠ THERE IS NO DELETE ANYWHERE IN HERE, AND THAT IS THE DESIGN. Each of these is a join key:
 * employees point at departments, marks point at subjects, timetable slots point at rooms, fee
 * invoices point at store items. Removing the row orphans the history. The verb is RETIRE — hide it
 * from every picker, keep it readable everywhere it was already used — and retiring is reversible,
 * because a mis-click that cannot be undone is the same trap one level down.
 */

export interface Named { id: string; name: string; isActive: boolean }

export interface SubjectRow {
  id: string; code: string; name: string; credits: number; semester: number; isActive: boolean; kind: string;
}
export interface ClassRow {
  id: string; branch: string; semester: number; section: string; label: string;
  isActive: boolean; isBatch: boolean; classTeacherId: string | null; classTeacherName: string | null;
}
export interface RoomRow { id: string; name: string; capacity: number | null; isActive: boolean }
export interface LeaveTypeRow {
  id: string; name: string; code: string | null; annualQuota: number;
  carryForward: boolean; maxCarryForward: number; isActive: boolean; isPaid: boolean;
}
export interface VehicleRow {
  id: string; name: string; registrationNumber: string | null; capacity: number;
  driverName: string | null; driverMobile: string | null; isActive: boolean;
}
export interface CampusRow { id: string; name: string; city: string | null; isActive: boolean }
export interface InstitutionRow {
  id: string; campusId: string; name: string; type: string; board: string; isActive: boolean;
  campusName?: string | null; campusCity?: string | null;
  /**
   * What the public website says about this one.
   * ⚠ `admissionsOpen` is NULLABLE and null means "whatever the trust says" — a school and a PU
   * college open in different months, and a plain boolean would force every institution to be set
   * before the page told the truth about any of them.
   */
  publicBlurb?: string | null; publicPhone?: string | null; admissionsOpen?: boolean | null;
}

/**
 * ⚠ `all: true` IS ONLY EVER PASSED BY THE SCREEN THAT MANAGES THESE. Every picker in the product
 * calls the same endpoints without it and keeps getting live rows only — a clerk assigning a
 * teacher to a department must never be offered a retired one. The flag exists so that a department
 * retired by mistake is still VISIBLE to the one screen that can bring it back; without it, the
 * mistake is invisible and therefore permanent.
 */
export const masterData = {
  // ---- academics ----
  subjects: (institutionId: string) =>
    api.get<SubjectRow[]>(`/api/subjects?institutionId=${institutionId}`).then(asList),
  updateSubject: (id: string, b: { name: string; credits: number; isActive: boolean; kind?: string }) =>
    api.put<SubjectRow>(`/api/subjects/${id}`, b),

  classes: (institutionId: string) =>
    api.get<ClassRow[]>(`/api/classes?institutionId=${institutionId}`).then(asList),
  renameClass: (id: string, b: { section?: string | null; batchName?: string | null }) =>
    api.put<ClassRow>(`/api/classes/${id}`, b),
  setClassActive: (id: string, state: boolean) =>
    api.put<ClassRow>(`/api/classes/${id}/active/${state}`),

  // ---- rooms ----
  rooms: (includeInactive = true) =>
    api.get<RoomRow[]>(`/api/timetable/rooms?includeInactive=${includeInactive}`).then(asList),
  updateRoom: (id: string, b: { institutionId: string; name: string; capacity: number | null }) =>
    api.put<RoomRow>(`/api/timetable/rooms/${id}`, b),
  setRoomActive: (id: string, state: boolean) =>
    api.put<RoomRow>(`/api/timetable/rooms/${id}/active/${state}`),

  // ---- HR config ----
  departments: (all = true) => api.get<Named[]>(`/api/hr/departments?all=${all}`).then(asList),
  updateDepartment: (id: string, b: { name: string; code: string | null }) =>
    api.put<Named>(`/api/hr/departments/${id}`, b),
  setDepartmentActive: (id: string, state: boolean) =>
    api.put<Named>(`/api/hr/departments/${id}/active/${state}`),

  designations: (all = true) => api.get<Named[]>(`/api/hr/designations?all=${all}`).then(asList),
  updateDesignation: (id: string, b: { name: string; code: string | null }) =>
    api.put<Named>(`/api/hr/designations/${id}`, b),
  setDesignationActive: (id: string, state: boolean) =>
    api.put<Named>(`/api/hr/designations/${id}/active/${state}`),

  leaveTypes: (all = true) => api.get<LeaveTypeRow[]>(`/api/hr/leave-types?all=${all}`).then(asList),
  updateLeaveType: (id: string, b: {
    name: string; code: string | null; annualQuota: number;
    carryForward: boolean; maxCarryForward: number; isPaid: boolean;
  }) => api.put<LeaveTypeRow>(`/api/hr/leave-types/${id}`, b),
  setLeaveTypeActive: (id: string, state: boolean) =>
    api.put<LeaveTypeRow>(`/api/hr/leave-types/${id}/active/${state}`),

  // ---- transport ----
  vehicles: (includeInactive = true) =>
    api.get<VehicleRow[]>(`/api/transport/vehicles?includeInactive=${includeInactive}`).then(asList),
  updateVehicle: (id: string, b: {
    name: string; registrationNumber: string | null; capacity: number;
    driverName: string | null; driverMobile: string | null;
  }) => api.put<VehicleRow>(`/api/transport/vehicles/${id}`, b),
  setVehicleActive: (id: string, state: boolean) =>
    api.put<VehicleRow>(`/api/transport/vehicles/${id}/active/${state}`),

  // ---- the trust itself ----
  campuses: () => api.get<CampusRow[]>('/api/campuses').then(asList),
  updateCampus: (id: string, b: { name: string; city: string | null }) =>
    api.put<CampusRow>(`/api/campuses/${id}`, b),
  setCampusActive: (id: string, state: boolean) =>
    api.put<CampusRow>(`/api/campuses/${id}/active/${state}`),

  institutions: () => api.get<InstitutionRow[]>('/api/institutions').then(asList),
  updateInstitution: (id: string, b: { name: string }) =>
    api.put<InstitutionRow>(`/api/institutions/${id}`, b),
  setInstitutionActive: (id: string, state: boolean) =>
    api.put<InstitutionRow>(`/api/institutions/${id}/active/${state}`),
  /** What the public page says about this institution. */
  updateInstitutionPublic: (id: string, b: {
    blurb: string | null; phone: string | null; admissionsOpen: boolean | null;
  }) => api.put<InstitutionRow>(`/api/institutions/${id}/public`, b),
};
